<template>
  <div class="wlg-home">
    <!-- Hero Section - Now Using Reusable Component -->
    <Hero
      :title="$t('homePage.hero.title')"
      :subtitle="$t('homePage.hero.subtitle')"
      :carouselEnabled="true"
      :slides="heroSlides"
      :slideInterval="5000"
      :button1="{
        label: $t('homePage.hero.cta_primary'),
        path: '/register/member',
        variant: 'primary'
      }"
      :button2="{
        label: $t('homePage.hero.cta_secondary'),
        path: '/search',
        variant: 'secondary'
      }"
    />

    <!-- Four Pillars Section -->
    <section class="pillars-section">
      <div class="container">
        <div class="section-header">
          <h2>{{ $t('homePage.pillars.title') }}</h2>
          <p>{{ $t('homePage.pillars.subtitle') }}</p>
        </div>

        <div class="pillars-grid">
          <div v-for="(category, index) in pillarsData" :key="category.id" class="pillar-card">
            <div class="pillar-icon-wrapper">
              <img :src="category.bgImage" :alt="category.name" class="pillar-image" />
            </div>
            <div class="pillar-content">
              <h3>{{ $t(`homePage.pillars.categories.${index}.name`) }}</h3>
              <p>{{ $t(`homePage.pillars.categories.${index}.description`) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works">
      <div class="container">
        <div class="section-header">
          <h2>{{ $t('homePage.howItWorks.title') }}</h2>
          <p>{{ $t('homePage.howItWorks.subtitle') }}</p>
        </div>

        <div class="steps-grid">
          <div v-for="(item, index) in stepsData" :key="index" class="step-card">
            <div class="step-number">{{ index + 1 }}</div>
            <h3>{{ $t(`homePage.howItWorks.steps.${index}.title`) }}</h3>
            <p>{{ $t(`homePage.howItWorks.steps.${index}.description`) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Professionals Section -->
    <section v-if="professionals.length > 0" class="professionals-section">
      <div class="container">
        <div class="section-header">
          <h2>{{ $t('homePage.professionals.title') }}</h2>
          <p>{{ $t('homePage.professionals.subtitle') }}</p>
        </div>

        <div class="professionals-grid">
          <div v-for="(professional, index) in professionals" :key="professional.id" class="professional-card-wrapper">
            <h3 class="professional-pillar-title">{{ professionalTitles[index] }}</h3>
            <router-link :to="`/user/${professional.username}`" class="professional-card">
              <div class="professional-cover">
                <img :src="professional.coverPhoto" :alt="professional.name" class="professional-cover-image" />
              </div>
              <div class="professional-profile">
                <div class="professional-image-wrapper">
                  <img :src="professional.image" :alt="professional.name" class="professional-image" />
                </div>
                <div class="professional-info">
                  <h3>{{ professional.name }}</h3>
                  <p class="specialty">{{ professional.specialty }}</p>
                  <p class="location">📍 {{ professional.location }}</p>
                  <span class="view-profile">{{ $t('homePage.professionals.viewProfile') }}</span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Goals Section -->
    <section class="goals-section">
      <div class="container">
        <div class="goals-wrapper">
          <div class="goals-content">
            <h2>{{ $t('homePage.goals.title') }}</h2>
            <p class="goals-intro">{{ $t('homePage.goals.subtitle') }}</p>

            <div class="goals-grid">
              <div v-for="(goal, index) in goalsData" :key="index" class="goal-item">
                <span class="goal-icon">→</span>
                <span>{{ $t(`homePage.goals.list.${index}`) }}</span>
              </div>
            </div>

            <router-link to="/how-it-works" class="btn btn-primary">{{ $t('homePage.goals.learnMore') }}</router-link>
          </div>

          <div class="goals-visual">
            <img :src="$t('homePage.goals.image')" alt="Wellness Goals" class="goals-image" />
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section (Extracted Component) -->
    <FinalCTA />

    <!-- Blog Section (Extracted Component) -->
    <!-- <BlogSection /> -->

    <!-- FAQ Section (Extracted Component) -->
    <FaqSection />
  </div>
</template>

<script>
/* eslint-disable */
import Hero from '../sections/Hero.vue';
import FinalCTA from '../sections/FinalCTA.vue';
import FaqSection from '../sections/FaqSection.vue';
import BlogSection from '../sections/BlogSection.vue';
import API from '../../api';
import axios from 'axios';

export default {
  name: 'Home',
  components: {
    Hero,
    FinalCTA,
    FaqSection,
    BlogSection
  },
  data() {
    return {
      professionals: [],
      professionalsLoading: false,
      professionalTitles: ['Movement', 'Meals', 'Mind', 'Money'],
      stepsData: [{ id: 'search' }, { id: 'connect' }, { id: 'achieve' }],
      goalsData: Array(12).fill(null)
    };
  },
  computed: {
    heroSlides() {
      return this.$t('homePage.hero.slides');
    },
    pillarsData() {
      return this.$t('homePage.pillars.categories');
    }
  },
  mounted() {
    this.fetchFeaturedProfessionals();
  },
  methods: {
    async fetchFeaturedProfessionals() {
      try {
        const env = process.env.VUE_APP_ENV || 'development';

        let usernamesByEnv = null;

        try {
          const localizationResponse = await axios.get(
            `${process.env.VUE_APP_STRAPI_URL || 'http://localhost:1342'}/api/website-localization`
          );

          const responseData = localizationResponse.data;
          if (responseData?.data) {
            const data = Array.isArray(responseData.data) ? responseData.data[0] : responseData.data;
            const translations = data?.attributes?.translations || data?.translations;
            const parsed = typeof translations === 'string' ? JSON.parse(translations) : translations;
            usernamesByEnv = parsed?.homePage?.professionals?.professionalUsernames || null;
          }
        } catch {
          // Strapi unavailable, fall through to $t() fallback
        }

        if (!usernamesByEnv) {
          usernamesByEnv = this.$t('homePage.professionals.professionalUsernames');
        }

        const usernames = usernamesByEnv[env] || usernamesByEnv.development;

        const response = await API.post('featured-profiles', {
          professionalUsernames: usernames
        });

        const data = response?.data?.professionals || [];

        this.professionals = data.map(p => ({
          id: p.id,
          username: p.username || '',
          name: p.name || '',
          specialty: p.approvedCategories || '',
          location: p.city || p.state || '',
          image: p.profileImageUrl || '',
          coverPhoto: p.backgroundImageUrl || ''
        }));
      } catch (err) {
        console.error('Error fetching featured professionals:', err);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.wlg-home {
  width: 100%;
  background: $bg-white;
  color: $text-dark;
  font-family: $font-family-body;
  line-height: 1.6;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 12px 32px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &.btn-primary {
    background: $brand-color;
    color: white;

    &:hover {
      background: $brand-dark;
      transform: translateY(-2px);
      box-shadow: 0 12px 24px rgba($brand-color, 0.2);
    }
  }

  &.btn-secondary {
    background: transparent;
    color: white;
    border: 2px solid white;

    &:hover {
      background: $brand-color;
    }
  }

  &.btn-lg {
    padding: 16px 48px;
    font-size: 1.1rem;
  }
}

/* Section Header */
.section-header {
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.1rem;
    color: $text-light;
    max-width: 500px;
    margin: 0 auto;
  }
}

/* Pillars Section */
.pillars-section {
  padding: 5rem 0;
  background: $bg-light;
}

.pillars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.pillar-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);

    .pillar-image {
      transform: scale(1.1);
    }
  }
}

.pillar-icon-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.pillar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.pillar-content {
  padding: 2rem;

  h3 {
    font-size: 1.4rem;
    margin-bottom: 0.75rem;
  }

  p {
    color: $text-light;
    font-size: 0.95rem;
    line-height: 1.6;
  }
}

/* How It Works */
.how-it-works {
  padding: 5rem 0;
  background: white;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.step-card {
  text-align: center;
  padding: 2.5rem 1.5rem;
  background: $bg-light;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
  }

  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.75rem;
  }

  p {
    color: $text-light;
    font-size: 0.95rem;
  }
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: $brand-color;
  color: white;
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

/* Professionals Section */
.professionals-section {
  padding: 5rem 0;
  background: $bg-light;

  @media (max-width: 968px) {
    padding: 1.5rem 0;
  }
}

.professionals-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  margin-top: 3rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.professional-card-wrapper {
  display: flex;
  flex-direction: column;
}

.professional-pillar-title {
  font-size: 1.6rem;
  font-weight: 900;
  color: $brand-color;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.professional-card {
  display: block;
  text-decoration: none;
  color: inherit;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);

    .professional-image {
      transform: scale(1.05);
    }
  }
}

.professional-cover {
  height: 120px;
  overflow: hidden;
  background: linear-gradient(135deg, $brand-color, $brand-dark);
}

.professional-cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.professional-profile {
  padding: 0 1.5rem 1.5rem;
  text-align: center;
}

.professional-image-wrapper {
  width: 100px;
  height: 100px;
  margin: -50px auto 1rem;
}

.professional-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.professional-info {
  h3 {
    font-size: 1.15rem;
    margin-bottom: 0.5rem;
  }

  .specialty {
    color: $brand-color;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .location {
    color: $text-light;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
}

.view-profile {
  color: $brand-color;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(4px);
  }
}

/* Goals Section */
.goals-section {
  padding: 5rem 0;
  background: white;

  @media (max-width: 968px) {
    padding: 3rem 0;
  }
}

.goals-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: stretch;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.goals-content {
  align-self: start;
}

.goals-content h2 {
  font-size: 2.8rem;
  margin-top: -10px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.goals-intro {
  color: $text-light;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.7;
}

.goals-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.2rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.goal-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
}

.goal-icon {
  color: $brand-color;
  font-weight: 800;
  flex-shrink: 0;
}

.goals-visual {
  animation: slideInRight 0.8s ease-out;
  position: relative;

  @media (max-width: 968px) {
    height: auto;
    aspect-ratio: 2 / 3;
  }
}

.goals-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

/* Animations */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
